import type { ReactNode } from 'react';

/**
 * KpiCard `fit` (the All-time revenue tile on the EVI team's dashboard,
 * Warren, 28 Oct 2026). The value stays on one line and its size follows
 * the tile's width, from the theme's own size down to FIT_MIN_VALUE_PX, so
 * a seven-figure total never breaks mid-number in a narrow desktop cell.
 *
 * The card is an inline-size query container, so 100cqi is its content
 * box. The size is that width over the value's length times
 * FIT_EM_PER_CHAR, which sits a little above the widest DM Sans tabular
 * run measured at weight 600: 0.444 em a character for "$1,284,310" and
 * 0.468 for "$12,843,100". The value therefore always fits with a few
 * pixels to spare, and a short value keeps the theme's full size.
 *
 * Pure, so the numbers are tested without a browser: kpiFit.test.ts.
 */
export const FIT_EM_PER_CHAR = 0.5;
export const FIT_MIN_VALUE_PX = 12;

/** Characters the fitted value is sized for. A value that is not plain
 *  text counts as ten; a unit suffix adds its length and a space. */
export function fitValueChars(value: ReactNode, unit?: string): number {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : null;
  const chars = text === null ? 10 : Math.max(1, text.length);
  return unit ? chars + unit.length + 1 : chars;
}

/** The CSS font-size of a fitted value: never above `maxPx`, never below FIT_MIN_VALUE_PX. */
export function fitValueFontSize(chars: number, maxPx: number): string {
  const ems = Math.round(Math.max(1, chars) * FIT_EM_PER_CHAR * 100) / 100;
  return `clamp(${FIT_MIN_VALUE_PX}px, calc(100cqi / ${ems}), ${maxPx}px)`;
}

/** The size CSS resolves for a content box `contentPx` wide, to 0.01 px. */
export function fittedValuePx(chars: number, maxPx: number, contentPx: number): number {
  const ems = Math.round(Math.max(1, chars) * FIT_EM_PER_CHAR * 100) / 100;
  return Math.round(Math.min(maxPx, Math.max(FIT_MIN_VALUE_PX, contentPx / ems)) * 100) / 100;
}
